import './styles/main.css';
import { SITE_LINKS } from './config/links';
import { PRODUCTS, type ProductId, type ProductStatus } from './data/products';

const getProduct = (id: ProductId) => {
  const product = PRODUCTS.find((item) => item.id === id);
  if (!product) throw new Error(`Missing product: ${id}`);
  return product;
};

const aiguide = getProduct('aiguide');
const acebook = getProduct('acebook');
const statusLabel = (status: ProductStatus) => (status === 'online' ? 'Online now' : 'Preparing');

const fieldItems = [
  ['01', '教育内容', 'CONTENT'],
  ['02', 'AI 学习', 'LEARNING'],
  ['03', '数学可视化', 'VISUAL'],
  ['04', '智能工具', 'TOOLS'],
] as const;

const productCards = PRODUCTS.map(
  (product, index) => `
    <article
      class="product-card product-card-${product.accent} product-card-${product.priority} reveal tilt-card"
      data-product-id="${product.id}"
      style="--reveal-delay: ${index * 90}ms"
    >
      <span class="product-card-grid" aria-hidden="true"></span>
      <div class="product-card-header">
        <span class="product-index">${product.indexLabel}</span>
        <span class="product-live"><i aria-hidden="true"></i>${statusLabel(product.status)}</span>
      </div>
      <a class="product-visual" href="${product.href}" aria-label="打开 ${product.name}">
        <span class="window-bar" aria-hidden="true"><i></i><i></i><i></i><b>${product.domain}</b></span>
        <img src="${product.screenshot}" alt="${product.screenshotAlt}" loading="lazy" decoding="async" />
        <span class="product-visual-glow" aria-hidden="true"></span>
        <span class="product-visual-caption" aria-hidden="true"><i></i> Live product <b>↗</b></span>
      </a>
      <div class="product-body">
        <div class="product-title-row">
          <div><span class="product-role">${product.role}</span><h3>${product.name}</h3></div>
          <span class="product-arrow" aria-hidden="true">↗</span>
        </div>
        <p class="product-tagline">${product.tagline}</p>
        <p class="product-problem">${product.problem}</p>
        <ul class="product-highlights" aria-label="${product.name} 产品亮点">
          ${product.highlights.map((highlight) => `<li>${highlight}</li>`).join('')}
        </ul>
      </div>
      <div class="product-footer">
        <span>${product.audience}</span>
        <a class="button button-product magnetic" href="${product.href}" data-product-cta="${product.id}">${product.ctaLabel}<span aria-hidden="true">↗</span></a>
      </div>
    </article>
  `,
).join('');

const fieldChips = fieldItems
  .map(
    ([index, label, code], itemIndex) => `
      <div class="field-chip reveal" style="--reveal-delay: ${itemIndex * 65}ms">
        <span>${index}</span>
        <i class="field-signal" aria-hidden="true"><b></b><b></b><b></b></i>
        <strong>${label}</strong>
        <small>${code}</small>
      </div>
    `,
  )
  .join('');

const particleMarkup = Array.from({ length: 14 }, (_, index) => {
  const x = 8 + ((index * 37) % 84);
  const y = 7 + ((index * 53) % 86);
  const delay = ((index * 0.43) % 4).toFixed(2);
  const duration = (3.8 + (index % 5) * 0.65).toFixed(2);
  return `<i style="--particle-x:${x}%;--particle-y:${y}%;--particle-delay:${delay}s;--particle-duration:${duration}s"></i>`;
}).join('');

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('Missing #app mount node');

const root = document.documentElement;
root.classList.add('js');
app.innerHTML = `
  <a class="skip-link" href="#products">跳到产品</a>
  <div class="scroll-progress" aria-hidden="true"><span></span></div>
  <div class="ambient ambient-one" aria-hidden="true"></div>
  <div class="ambient ambient-two" aria-hidden="true"></div>
  <main class="page-shell">
    <header class="site-header" aria-label="主导航">
      <a class="brand" href="${SITE_LINKS.home}" aria-label="Zhongbin Zhang 首页">
        <span class="brand-mark"><i aria-hidden="true"></i>ZZB</span>
        <span class="brand-copy"><strong>Zhongbin Zhang</strong><small>Independent product developer</small></span>
      </a>
      <nav class="nav-links" aria-label="页面导航">
        <a href="${SITE_LINKS.products}" data-nav-target="products">产品</a>
        <a href="${SITE_LINKS.about}" data-nav-target="about">关于</a>
        <a href="${SITE_LINKS.contact}" data-nav-target="contact">联系</a>
      </nav>
      <a class="header-launch magnetic" href="${aiguide.href}" data-product-cta="header-aiguide">AIGuide <span aria-hidden="true">↗</span></a>
    </header>

    <section class="hero" id="about" aria-labelledby="hero-title">
      <span class="hero-spotlight" aria-hidden="true"></span>
      <div class="hero-copy reveal">
        <div class="hero-kicker"><span aria-hidden="true"></span> AI × Education × Visualization</div>
        <h1 id="hero-title"><span>把复杂知识，</span><em><span>做成真正</span><span>能用的产品</span></em></h1>
        <p class="hero-subtitle">独立产品开发者 · AI × 教育 × 可视化</p>
        <p class="hero-description">从学习路径、内容生产到交互工具，构建让学生愿意打开、能够完成、可以持续使用的产品。</p>
        <div class="hero-actions" aria-label="首屏操作">
          <a class="button button-primary magnetic" href="${aiguide.href}" data-product-cta="hero-aiguide">探索 AIGuide <span aria-hidden="true">↗</span></a>
          <a class="button button-ghost" href="#products">查看产品矩阵 <span aria-hidden="true">↓</span></a>
        </div>
        <div class="hero-proof" aria-label="产品状态">
          <span><strong>02</strong> Products online</span><span><strong>01</strong> Unified vision</span><span><strong>∞</strong> Iterating</span>
        </div>
      </div>

      <div class="hero-stage reveal" role="group" aria-label="AIGuide 与 ACEbook 产品轨道">
        <span class="stage-cursor-glow" aria-hidden="true"></span>
        <div class="stage-world">
          <div class="stage-grid" aria-hidden="true"></div>
          <div class="stage-particles" aria-hidden="true">${particleMarkup}</div>
          <div class="orbit orbit-outer" aria-hidden="true"></div>
          <div class="orbit orbit-mid" aria-hidden="true"></div>
          <div class="orbit orbit-inner" aria-hidden="true"></div>
          <div class="core" aria-hidden="true"><span>BUILDING</span><strong>AI</strong><small>LEARNING<br />PRODUCTS</small></div>
          <a class="orbit-node node-aiguide" href="${aiguide.href}" aria-label="打开 AIGuide"><span>01</span><strong>AIGuide</strong><small>Project learning</small></a>
          <a class="orbit-node node-acebook" href="${acebook.href}" aria-label="打开 ACEbook"><span>02</span><strong>ACEbook</strong><small>Interactive content</small></a>
          <div class="orbit-node node-system" aria-hidden="true"><span>∞</span><strong>System</strong><small>Always iterating</small></div>
          <div class="stage-coordinate coordinate-top">TWO PRODUCTS / ONLINE</div>
          <div class="stage-coordinate coordinate-bottom">PRODUCT SYSTEM / 2026</div>
          <div class="scan-line" aria-hidden="true"></div>
        </div>
      </div>
      <div class="hero-scroll-cue" aria-hidden="true"><span></span> Scroll to explore</div>
    </section>

    <div class="field-strip" id="field" aria-label="产品领域">
      <div class="field-heading reveal"><span>FOCUS AREAS</span><strong>从内容到智能工具的完整链路</strong><small>01—04 / PRODUCT CAPABILITIES</small></div>
      <div class="field-grid">${fieldChips}</div>
    </div>

    <section class="products-section" id="products" aria-labelledby="products-title">
      <div class="section-heading reveal">
        <div><span class="section-index">Selected work / 2026</span><h2 id="products-title">正在持续迭代的<br /><em>两款在线产品</em></h2></div>
        <p>AIGuide 负责把 AI 学习变成可完成的项目路径；ACEbook 负责承载清晰、稳定、可持续更新的内容体验。</p>
      </div>
      <div class="product-grid">${productCards}</div>
    </section>

    <section class="contact-panel reveal" id="contact" aria-labelledby="contact-title">
      <div class="contact-ticker" aria-hidden="true"><div><span>AI EDUCATION · PROJECT LEARNING · CONTENT SYSTEM · PRODUCT BUILDING · </span><span>AI EDUCATION · PROJECT LEARNING · CONTENT SYSTEM · PRODUCT BUILDING · </span></div></div>
      <div class="contact-orbit" aria-hidden="true"><span></span><span></span><span></span></div>
      <div class="contact-copy"><span class="section-index">Open for conversation</span><h2 id="contact-title">让下一个 AI 学习想法，<em>从概念变成产品。</em></h2><p>交流 AI 教育、项目式学习、竞赛辅导与内容产品合作。</p></div>
      <div class="contact-actions">
        <a class="button button-light magnetic" href="${SITE_LINKS.email}" data-contact-cta="email">开始交流 <span aria-hidden="true">↗</span></a>
        <a class="text-link" href="${SITE_LINKS.github}" aria-label="查看 Zhongbin Zhang 的 GitHub">GitHub <span aria-hidden="true">→</span></a>
        <a class="text-link" href="${aiguide.href}" data-product-cta="contact-aiguide">先看 AIGuide <span aria-hidden="true">→</span></a>
      </div>
    </section>

    <footer class="footer"><span>© 2026 Zhongbin Zhang</span><span>AI learning products · Independent / Remote</span><a href="#about">Back to top ↑</a></footer>
  </main>
`;

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
const navLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('[data-nav-target]'));
const trackedSections = ['about', 'products', 'contact']
  .map((id) => document.getElementById(id))
  .filter((section): section is HTMLElement => Boolean(section));

const updateActiveNavigation = () => {
  const marker = window.innerHeight * 0.38;
  let activeSection = trackedSections[0];
  for (const section of trackedSections) {
    if (section.getBoundingClientRect().top <= marker) activeSection = section;
  }
  for (const link of navLinks) {
    const isActive = link.dataset.navTarget === activeSection?.id;
    link.classList.toggle('is-active', isActive);
    if (isActive) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  }
};

let scrollFrame = 0;
const updateScrollEffects = () => {
  const maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
  root.style.setProperty('--scroll-progress', Math.min(window.scrollY / maxScroll, 1).toFixed(4));
  root.style.setProperty('--scroll-offset', `${Math.min(window.scrollY, 1600)}px`);
  updateActiveNavigation();
  scrollFrame = 0;
};
const queueScrollEffects = () => {
  if (scrollFrame) return;
  scrollFrame = window.requestAnimationFrame(updateScrollEffects);
};
window.addEventListener('scroll', queueScrollEffects, { passive: true });
window.addEventListener('resize', queueScrollEffects);
updateScrollEffects();

const revealAll = () => {
  document.querySelectorAll<HTMLElement>('.reveal').forEach((element) => element.classList.add('is-visible'));
};

if (reducedMotionQuery.matches || !('IntersectionObserver' in window)) {
  revealAll();
} else {
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
  );
  document.querySelectorAll<HTMLElement>('.reveal').forEach((element) => observer.observe(element));
}

const hero = document.querySelector<HTMLElement>('.hero');
const resetHeroPointer = () => {
  hero?.style.setProperty('--stage-rx', '0deg');
  hero?.style.setProperty('--stage-ry', '0deg');
  hero?.style.setProperty('--stage-tx', '0px');
  hero?.style.setProperty('--stage-ty', '0px');
  hero?.style.setProperty('--pointer-x', '72%');
  hero?.style.setProperty('--pointer-y', '32%');
};

if (hero && !reducedMotionQuery.matches && finePointerQuery.matches) {
  let pointerFrame = 0;
  let pointerX = 0;
  let pointerY = 0;
  hero.addEventListener('pointermove', (event) => {
    const rect = hero.getBoundingClientRect();
    pointerX = (event.clientX - rect.left) / rect.width;
    pointerY = (event.clientY - rect.top) / rect.height;
    if (pointerFrame) return;
    pointerFrame = window.requestAnimationFrame(() => {
      const x = pointerX - 0.5;
      const y = pointerY - 0.5;
      hero.style.setProperty('--stage-rx', `${(-y * 3).toFixed(2)}deg`);
      hero.style.setProperty('--stage-ry', `${(x * 4).toFixed(2)}deg`);
      hero.style.setProperty('--stage-tx', `${(x * 10).toFixed(2)}px`);
      hero.style.setProperty('--stage-ty', `${(y * 10).toFixed(2)}px`);
      hero.style.setProperty('--pointer-x', `${(pointerX * 100).toFixed(1)}%`);
      hero.style.setProperty('--pointer-y', `${(pointerY * 100).toFixed(1)}%`);
      pointerFrame = 0;
    });
  });
  hero.addEventListener('pointerleave', resetHeroPointer);
}

if (!reducedMotionQuery.matches && finePointerQuery.matches) {
  document.querySelectorAll<HTMLElement>('.tilt-card').forEach((card) => {
    card.addEventListener('pointermove', (event) => {
      const rect = card.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      card.style.setProperty('--tilt-x', `${(-y * 2.4).toFixed(2)}deg`);
      card.style.setProperty('--tilt-y', `${(x * 3.2).toFixed(2)}deg`);
      card.style.setProperty('--glow-x', `${((x + 0.5) * 100).toFixed(1)}%`);
      card.style.setProperty('--glow-y', `${((y + 0.5) * 100).toFixed(1)}%`);
      card.style.setProperty('--sheen-opacity', '1');
    });
    card.addEventListener('pointerleave', () => {
      card.style.setProperty('--tilt-x', '0deg');
      card.style.setProperty('--tilt-y', '0deg');
      card.style.setProperty('--sheen-opacity', '0');
    });
  });

  document.querySelectorAll<HTMLElement>('.magnetic').forEach((element) => {
    element.addEventListener('pointermove', (event) => {
      const rect = element.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      element.style.setProperty('--magnet-x', `${(x * 5).toFixed(2)}px`);
      element.style.setProperty('--magnet-y', `${(y * 4).toFixed(2)}px`);
    });
    element.addEventListener('pointerleave', () => {
      element.style.setProperty('--magnet-x', '0px');
      element.style.setProperty('--magnet-y', '0px');
    });
  });
}

window.requestAnimationFrame(() => root.classList.add('page-ready'));
